import { useCallback, useEffect } from "react";
import { ForestOwner, CreateForestOwner, UpdateForestOwner } from "../types/models/ForestOwner.d";
import { UpdateEntityForestOwner } from "../types/models/ForestOwner";
import { useGetForestOwnerListQuery, useInsertForestOwnerMutation, useUpdateForestOwnerMutation, useDeleteForestOwnerMutation, } from "../apis/ForestOwner.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useForestOwner(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertForestOwnerMutation, { 
        isLoading: insertForestOwnerLoading, 
        isSuccess: insertForestOwnerIsSuccess,
        isError: insertForestOwnerIsError,
        error: insertForestOwnerError,
    }] = useInsertForestOwnerMutation()
    const [updateForestOwnerMutation, { 
        isLoading: updateForestOwnerLoading,
        isSuccess: updateForestOwnerIsSuccess,
        isError: updateForestOwnerIsError,
        error: updateForestOwnerError,
    }] = useUpdateForestOwnerMutation()
    const [deleteForestOwnerMutation, { 
        isLoading: deleteForestOwnerLoading,
        isSuccess: deleteForestOwnerIsSuccess,
        isError: deleteForestOwnerIsError,
        error: deleteForestOwnerError,
    }] = useDeleteForestOwnerMutation()

    const loading =
        insertForestOwnerLoading ||
        updateForestOwnerLoading ||
        deleteForestOwnerLoading ||
        false

    const insertForestOwner = useCallback(async (forestOwner: CreateForestOwner, redirectPath: string|undefined) => {
        try {
            const data = await insertForestOwnerMutation(forestOwner).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertForestOwnerMutation, navigate])
    
    useEffect(() => {
        if (insertForestOwnerIsSuccess) {
            openSuccessAlert('Forest Owner created')
        } else if (insertForestOwnerIsError) {
            console.log("add error", insertForestOwnerError)
            openFailAlert(t`Forest Owner add error: ` + insertForestOwnerError?.data?.message)
        }
    }, [
        insertForestOwnerIsSuccess,
        insertForestOwnerIsError,
        insertForestOwnerError
    ]);

    const updateForestOwner = useCallback(async (forestOwner: UpdateEntityForestOwner, redirectPath: string|undefined) => {
        try {
            const data = await updateForestOwnerMutation(forestOwner).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateForestOwnerMutation, navigate])
    
    useEffect(() => {
        if (updateForestOwnerIsSuccess) {
            openSuccessAlert(t`Forest Owner updated`)
        } else if (updateForestOwnerIsError) {
            console.log("update error", updateForestOwnerError)
            openFailAlert(t`Forest Owner update error: ` + updateForestOwnerError?.data?.message)
        }
    }, [
        updateForestOwnerIsSuccess,
        updateForestOwnerIsError,
        updateForestOwnerError
    ]);

    const deleteForestOwner = useCallback((forestOwner: ForestOwner, redirectPath: string|undefined) => {
        openDialog(async () => {
            forestOwner = {
                ...forestOwner,
            }
            try {
                const { data } = await deleteForestOwnerMutation(forestOwner);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteForestOwnerMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteForestOwnerIsSuccess) {
            openSuccessAlert(t`Forest Owner deleted`)
        } else if (deleteForestOwnerIsError) {
            console.log("delete error", deleteForestOwnerError)
            openFailAlert(t`Forest Owner delete error: ` + deleteForestOwnerError?.data?.message)
        }
    }, [
        deleteForestOwnerIsSuccess,
        deleteForestOwnerIsError,
        deleteForestOwnerError
    ]);

    return {
        loading,
        insertForestOwner,
        updateForestOwner,
        deleteForestOwner
    }
}
